import { getAuth } from 'firebase/auth';
import { auth as rebuildAuth } from '../services/authService';
import { logDebug, logError } from '../shared/common/logger';


class RequestQueue {
	private queue: (() => Promise<any>)[] = [];
	private isProcessing = false;

	async addToQueue(request: () => Promise<any>): Promise<any> {
		return new Promise((resolve, reject) => {
			this.queue.push(async () => {
				try {
					const result = await request();
					resolve(result);
				} catch (error) {
					reject(error);
				}
			});
			this.processQueue();
		});
	}

	private async processQueue() {
		if (this.isProcessing || this.queue.length === 0) {
			return;
		}

		this.isProcessing = true;
		const nextRequest = this.queue.shift();
		if (nextRequest) {
			await nextRequest();
		}
		this.isProcessing = false;
		this.processQueue();
	}
}

interface IFetchAPIProps {
	method?: string,
	params?: any,
	dataField?: string,
	sendAuth?: boolean,
	noLog?: boolean
}

export default class RestAPI {
	private static _requestQueue = new RequestQueue();
	public static SERVER_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8082' : 'https://api.rebuild.work');
	public static API_URL = `${RestAPI.SERVER_BASE_URL}/rest/test`;

	public static getServerBaseUrl(path: string): string {
		if (path.startsWith('/')) {
			path = path.substring(1);
		}
		return `${RestAPI.SERVER_BASE_URL}/${path}`;
	}

	static async fetchAPI2(api: string, props: IFetchAPIProps): Promise<any> {
		return this.fetchAPI(api, props.method ?? 'GET', props.params ?? {}, props.dataField ?? 'data', props.sendAuth ?? true, props.noLog ?? false);
	}
	static async fetchAPI(api: string, method = 'GET', params: any = {}, dataField = 'data', sendAuth = true, noLog?: boolean): Promise<any> {

		if (Object.keys(params).length > 0 && method === 'GET') {
			api += '?';
			Object.entries(params).forEach(([key, value]) => {
				api += `${key}=${value}&`;
			});
			params = {};
		}
		const newURL = api.startsWith('/rest/') ? `${RestAPI.SERVER_BASE_URL}${api}` : `${RestAPI.API_URL}/${api}`;
		const retData = await this.fetchURL(newURL, method, params, dataField, sendAuth, !!noLog);
		!noLog && logDebug('fetchAPI', api, method, params, 'response:', retData);
		return retData;
	}

	/**
	 * Fetch API from the Rebuild backend (apiserver /rest/v2 endpoints)
	 * Automatically fetches and uses the Firebase authentication bearer token
	 */
	static async fetchRebuildAPI(api: string, method = 'GET', params: any = {}, dataField = 'data', sendAuth = true, noLog?: boolean): Promise<any> {
		if (Object.keys(params).length > 0 && method === 'GET') {
			api += '?';
			Object.entries(params).forEach(([key, value]) => {
				api += `${key}=${value}&`;
			});
			params = {};
		}
		const rebuildAPIUrl = `${RestAPI.SERVER_BASE_URL}/rest/v2`;
		const newURL = api.startsWith('/rest/') ? `${RestAPI.SERVER_BASE_URL}${api}` : `${rebuildAPIUrl}/${api}`;

		let bearerToken: string | undefined;
		if (sendAuth) {
			const token = await this.getRebuildIdToken();
			if (token) {
				bearerToken = `Bearer ${token}`;
			}
		}
		const retData = await this.fetchURL(newURL, method, params, dataField, sendAuth, !!noLog, bearerToken);
		!noLog && logDebug('fetchRebuildAPI', api, method, params, 'response:', retData);
		return retData;
	}

	static async getIdToken(): Promise<string | null> {
		if (getAuth().currentUser) {
			try {
				return await getAuth().currentUser!.getIdToken();
			} catch (err) {
				logError('Error in getIdToken :', err);
			}
		}
		return null;
	}

	/**
	 * Get the ID token from the Rebuild auth instance (authApp)
	 */
	static async getRebuildIdToken(): Promise<string | null> {
		if (rebuildAuth.currentUser) {
			try {
				return await rebuildAuth.currentUser.getIdToken();
			} catch (err) {
				logError('Error in getRebuildIdToken :', err);
			}
		}
		return null;
	}

	static async getBearerHeaders(): Promise<any> {
		const authToken = await this.getIdToken() || '';
		return {
			'authorization': 'Bearer ' + authToken,
			'uid': getAuth().currentUser!.uid,
		};
	}

	private static _useQueue = false;
	static async fetchURL(url: URL | string, method = 'GET', params: any = {}, dataField = 'data', sendAuth = true, noLog = false, bearerToken?: string): Promise<any> {
		const request = async () => {
			const isFormData = params instanceof FormData;
			if (typeof url === 'string') {
				url = new URL(url);
			}
			let authorizationHeader = '';
			let uid = 'none';
			let response;
			if (sendAuth) {
				if (bearerToken) {
					authorizationHeader = bearerToken;
					uid = 'none';
				} else {
					const auth = getAuth();
					const authToken = await this.getIdToken() || '';
					authorizationHeader = 'Bearer ' + authToken;
					uid = auth.currentUser?.uid ?? 'none';
				}
			}
			const methodsWithBody = ['POST', 'PUT', 'PATCH'];
			const methodUpper = method.toUpperCase();

			if (methodsWithBody.includes(methodUpper)) {
				const requestOptions: RequestInit = {
					method,
					headers: isFormData ? {
						'authorization': authorizationHeader,
						'uid': uid,
					} : {
						'Content-Type': 'application/json;charset=UTF-8',
						'Accept': 'application/json',
						'authorization': authorizationHeader,
						'uid': uid,
					},
				};

				if (isFormData) {
					requestOptions.body = params;
				} else if (Object.keys(params).length > 0) {
					requestOptions.body = JSON.stringify(params);
				}

				response = await fetch(url, requestOptions);
			} else {
				try {
					response = await fetch(url, {
						method,
						headers: {
							'Content-Type': 'application/json;charset=UTF-8',
							'Accept': 'application/json',
							'authorization': authorizationHeader,
							'uid': uid
						},
					});
				} catch (error) {
					return null;
				}
			}
			try {
				const data: any = await response.json();
				if (!data.success) {
					logError('Error in fetchURL :', data.error);
					return null;
				}
				if (dataField && dataField in data) {
					return data[dataField];
				}
				return data;
			}
			catch (error) {
				try {
					const text: any = await response.text();
					return text;
				} catch (err) {
					logError('Error reading text from url :', err);
				}
			}
			return null
		}
		if (RestAPI._useQueue) {
			return RestAPI._requestQueue.addToQueue(request);
		} else {
			return request();
		}
	}
}
